"use client"
import { SearchBar } from '@/components/ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Text,
  TextField
} from '@radix-ui/themes';
import { Filter, Plus } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

export interface ServiceMetaFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedDateRange: string;
  dateFrom?: string;
  dateTo?: string;
}

const statuses = ['All', 'Published', 'Draft'];
const dateRanges = ['All Time', 'Today', 'This Week', 'This Month', 'Last 3 Months'];

interface ServiceMetaFiltersProps {
  onFiltersChange: (filters: ServiceMetaFilters) => void;
  onClearFilters?: () => void;
}

export default function ServiceMetaFilters({ onFiltersChange, onClearFilters }: ServiceMetaFiltersProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Memoize filters object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => ({
    searchTerm,
    selectedStatus,
    selectedDateRange,
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  }), [searchTerm, selectedStatus, selectedDateRange, dateFrom, dateTo]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange(memoizedFilters);
  }, [memoizedFilters, onFiltersChange]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('All');
    setSelectedDateRange('All Time');
    setDateFrom('');
    setDateTo('');
    onClearFilters?.();
  };

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <Box className="space-y-4">
        {/* Main Search */}
        <Flex align="center" gap="4">
          <Box className="flex-1 max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search service meta by name, slug, or content..."
            />
          </Box>

          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>

          <Link href="/admin/service-meta/new">
            <Button>
              <Plus className="h-4 w-4 " />
              Create Service Meta
            </Button>
          </Link>
        </Flex>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-4 border-t border-gray-200">
            {/* Status Filter */}
            <Box className='col-span-3'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Status
              </Text>
              <Select.Root value={selectedStatus} onValueChange={setSelectedStatus}>
                <Select.Trigger className="w-full" />
                <Select.Content >
                  {statuses.map(status => (
                    <Select.Item key={status} value={status}>
                      {status}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Date Range Filter */}
            <Box className='col-span-4'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Date Range
              </Text>
              <Select.Root value={selectedDateRange} onValueChange={setSelectedDateRange}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {dateRanges.map(range => (
                    <Select.Item key={range} value={range}>
                      {range}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Custom Date Range */}
            <Box className='col-span-5'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Custom Range
              </Text>
              <Flex gap="2">
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Flex>
            </Box>
          </div>
        )}

        {/* Active Filters Display */}
        {(selectedStatus !== 'All' || selectedDateRange !== 'All Time') && (
          <Flex align="center" gap="2" className="pt-4 border-t border-gray-200">
            <Text size="2" className="text-gray-500">Active filters:</Text>
            {selectedStatus !== 'All' && (
              <Badge variant="soft" color="blue">
                Status: {selectedStatus}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedStatus('All')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedDateRange !== 'All Time' && (
              <Badge variant="soft" color="purple">
                Date: {selectedDateRange}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedDateRange('All Time')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="1"
              color="red"
              onClick={clearFilters}
            >
              Clear all
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}